'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  decodeAnimatedWebpFrames,
  extractPaletteEntriesFromRgba,
  formatFileSize,
  paletteEntriesToCssVariables,
  paletteEntriesToJson,
  renderStillWebpImageData,
  type DecodedWebpFrame,
} from '@/lib/browser-webp-tools';

type PaletteLimit = 'all' | '256' | '128' | '64';

function createDownloadUrl(content: string, mimeType: string) {
  return URL.createObjectURL(new Blob([content], { type: mimeType }));
}

export default function BrowserWebpPaletteExtractor() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [frames, setFrames] = useState<DecodedWebpFrame[]>([]);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(0);
  const [entries, setEntries] = useState<ReturnType<typeof extractPaletteEntriesFromRgba>>([]);
  const [hexUrl, setHexUrl] = useState<string | null>(null);
  const [cssUrl, setCssUrl] = useState<string | null>(null);
  const [jsonUrl, setJsonUrl] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [paletteLimit, setPaletteLimit] = useState<PaletteLimit>('256');

  useEffect(() => {
    return () => {
      [hexUrl, cssUrl, jsonUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [cssUrl, hexUrl, jsonUrl]);

  function resetOutputState() {
    [hexUrl, cssUrl, jsonUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setFrames([]);
    setSelectedFrameIndex(0);
    setEntries([]);
    setHexUrl(null);
    setCssUrl(null);
    setJsonUrl(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be opened.');
    }
  }

  async function updateEntries(activeFrames: DecodedWebpFrame[]) {
    if (!source) {
      return;
    }

    const rgba =
      source.metadata.animated && activeFrames.length
        ? (activeFrames[selectedFrameIndex]?.rgba ?? activeFrames[0]!.rgba)
        : (await renderStillWebpImageData(source.file)).data;
    const limit = paletteLimit === 'all' ? null : Number(paletteLimit);
    const nextEntries = extractPaletteEntriesFromRgba(rgba, limit);
    const hexText = nextEntries.map((entry) => entry.color).join('\n');
    const cssText = paletteEntriesToCssVariables(nextEntries, 'webp-color');
    const jsonText = paletteEntriesToJson(nextEntries);

    [hexUrl, cssUrl, jsonUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setEntries(nextEntries);
    setHexUrl(createDownloadUrl(hexText, 'text/plain;charset=utf-8'));
    setCssUrl(createDownloadUrl(cssText, 'text/css;charset=utf-8'));
    setJsonUrl(createDownloadUrl(jsonText, 'application/json;charset=utf-8'));
  }

  async function runExtraction() {
    if (!source) {
      setErrorMessage('Choose one WebP first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'webp-palette-extractor',
        'Credits are not available for another WebP palette extraction right now.',
      );
      const nextFrames = source.metadata.animated
        ? await decodeAnimatedWebpFrames(source.file)
        : [];
      setFrames(nextFrames);
      await updateEntries(nextFrames);
      setCreditMessage(formatToolCreditSuccess('WebP palette extraction', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The WebP palette could not be extracted.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const paletteCount = entries.length;
  const palettePreview = useMemo(() => entries.slice(0, 48), [entries]);

  async function copyText(type: 'hex' | 'css' | 'json') {
    if (!entries.length) {
      return;
    }

    const text =
      type === 'hex'
        ? entries.map((entry) => entry.color).join('\n')
        : type === 'css'
          ? paletteEntriesToCssVariables(entries, 'webp-color')
          : paletteEntriesToJson(entries);

    try {
      await navigator.clipboard.writeText(text);
      setCreditMessage(
        type === 'hex'
          ? 'HEX palette copied.'
          : type === 'css'
            ? 'CSS variables copied.'
            : 'JSON palette copied.',
      );
    } catch {
      setErrorMessage('The browser could not copy the palette export.');
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Extract the color palette from one WebP</h2>
          <p>
            Inspect WebP colors by frequency, copy HEX values, and export CSS variables or JSON
            locally in the browser.
          </p>
        </div>
        <span className="tool-live-badge">Palette</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one WebP or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still and animated WebP are supported'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Palette limit</span>
              <select
                onChange={(event) => {
                  setPaletteLimit(event.target.value as PaletteLimit);
                  void updateEntries(frames);
                }}
                value={paletteLimit}
              >
                <option value="all">All</option>
                <option value="256">256</option>
                <option value="128">128</option>
                <option value="64">64</option>
              </select>
            </label>
            {source?.metadata.animated && frames.length ? (
              <label className="tool-live-field">
                <span>Frame</span>
                <select
                  onChange={(event) => {
                    setSelectedFrameIndex(Number(event.target.value));
                    void updateEntries(frames);
                  }}
                  value={selectedFrameIndex}
                >
                  {frames.map((frame, index) => (
                    <option key={frame.index} value={index}>
                      {`Frame ${frame.index}`}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runExtraction()}
              type="button"
            >
              {isProcessing ? 'Extracting…' : 'Extract palette'}
            </button>
            {hexUrl ? (
              <a className="button button-ghost" download="webp-palette.txt" href={hexUrl}>
                Download HEX
              </a>
            ) : null}
            {cssUrl ? (
              <a className="button button-ghost" download="webp-palette.css" href={cssUrl}>
                Download CSS
              </a>
            ) : null}
            {jsonUrl ? (
              <a className="button button-ghost" download="webp-palette.json" href={jsonUrl}>
                Download JSON
              </a>
            ) : null}
          </div>

          {entries.length ? (
            <div className="hero-actions tool-live-actions">
              <button
                className="button button-ghost"
                onClick={() => void copyText('hex')}
                type="button"
              >
                Copy HEX
              </button>
              <button
                className="button button-ghost"
                onClick={() => void copyText('css')}
                type="button"
              >
                Copy CSS
              </button>
              <button
                className="button button-ghost"
                onClick={() => void copyText('json')}
                type="button"
              >
                Copy JSON
              </button>
            </div>
          ) : null}

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Palette summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Colors</strong>
              <span>{paletteCount || 'Not extracted yet'}</span>
            </div>
            <div>
              <strong>Frame</strong>
              <span>
                {source?.metadata.animated ? `Frame ${selectedFrameIndex + 1}` : 'Still image'}
              </span>
            </div>
            <div>
              <strong>Display limit</strong>
              <span>{paletteLimit === 'all' ? 'All detected colors' : paletteLimit}</span>
            </div>
          </div>
        </aside>
      </div>

      {palettePreview.length ? (
        <div className="tool-live-preview">
          <h3>Palette preview</h3>
          <div className="tool-png-palette-grid">
            {palettePreview.map((entry) => (
              <div className="tool-png-palette-card" key={entry.key}>
                <div className="tool-png-palette-chip" style={{ background: entry.color }} />
                <strong>{entry.color}</strong>
                <span>{`${entry.percent}%`}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}
